import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { SEVERITY_LABELS, type LatencyFigure, type PanelistRow, type Scoreboard, type ScoreboardRow } from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { sortRows, type SortReaders } from '../../../components/data-table/sort-rows';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { TableSection } from '../../../components/TableSection';
import { EMPTY } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanView } from '../../../stores/session';
import { useScoreboard, type AnalyticsUrl } from '../api/analytics-api';
import { AnalyticsFilters } from '../components/AnalyticsFilters';
import { DictionaryLine, DrillCount, DrillLatency, ExportLinks, HowCounted } from '../components/AnalyticsBits';
import styles from '../Reports.module.css';

const DEPARTMENTS = 'department_scoreboard';
const PANELISTS = 'panelist_activity';

/**
 * RPT-04 — the department scoreboard and panelist activity (master plan S29, §L27 RPT-04a/b): the
 * other half of accountability. The HR Lead sees every department; a hiring manager their own
 * (DECISIONS-LOG S29–S32 Q3). Overrides, waivers and skips sit here too (Q5).
 */
export function ScoreboardPage(): ReactNode {
  const canView = useCanView('reports_own');
  const search: AnalyticsUrl = useSearch({ from: '/app-layout/reports/scoreboard' });
  const navigate = useNavigate();
  const report = useScoreboard(search, canView);
  const data = report.data;

  if (!canView || report.error?.status === 403) return <ForbiddenState resource="the department scoreboard" contact="Your admin" />;
  if (report.error && !data) return <ServerErrorState onRetry={() => void report.refetch()} />;

  const setFilters = (next: AnalyticsUrl): void => void navigate({ to: '/reports/scoreboard', search: next, replace: true });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Department scoreboard</h1>
          <p className={styles.lead}>What the departments, their approvers and their panels took — published beside the recruiters’ profiles, because fairness is both sides.</p>
        </div>
        {data && <ExportLinks report={DEPARTMENTS} filters={data.filters} />}
      </header>

      {data && <AnalyticsFilters filters={data.filters} audience={data.audience} onChange={setFilters} />}

      {!data ? (
        <Card>
          <SkeletonLines count={8} />
        </Card>
      ) : (
        <Tables data={data} />
      )}
    </div>
  );
}

function Tables({ data }: { readonly data: Scoreboard }): ReactNode {
  const filters = data.filters;
  return (
    <>
      <DictionaryLine filters={filters} version={data.dictionaryVersion} publishedOn={data.dictionaryPublishedOn} />

      <DepartmentTables data={data} />

      <PanelistTable data={data} />
    </>
  );
}

type LatencyKey = 'approvalLatency' | 'decisionLatency' | 'scorecardLag';
type CountKey = 'panelistNoShows' | 'reschedulesInitiated' | 'requisitionsRaised' | 'cancelledAfterStart' | 'overrides' | 'waivers' | 'skips';

/** The department tables as tables, not cards (DECISIONS-LOG 2026-10-06). Each figure still opens its records. */
function DepartmentTables({ data }: { readonly data: Scoreboard }): ReactNode {
  const filters = data.filters;
  const [departmentSort, setDepartmentSort] = useState<readonly SortSpec[]>([]);
  const [overrideSort, setOverrideSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);

  const latencyColumn = (id: LatencyKey, header: string): Column<ScoreboardRow> => ({
    id,
    header,
    isSortable: true,
    defaultWidth: 200,
    cell: (row) => <DrillLatency figure={row[id]} report={DEPARTMENTS} filters={filters} />,
    exportValue: (row) => latencyText(row[id]),
  });
  const countColumn = (id: CountKey, header: string): Column<ScoreboardRow> => ({
    id,
    header,
    isNumeric: true,
    isSortable: true,
    defaultWidth: 150,
    cell: (row) => <DrillCount figure={row[id]} report={DEPARTMENTS} filters={filters} />,
    exportValue: (row) => row[id].value,
  });
  const department: Column<ScoreboardRow> = { id: 'department', header: 'Department', isIdentity: true, isSortable: true, defaultWidth: 200, cell: (row) => row.departmentName };

  const departmentColumns: readonly Column<ScoreboardRow>[] = [
    department,
    { id: 'venture', header: 'Venture', isSortable: true, defaultWidth: 150, cell: (row) => row.ventureName },
    { id: 'managers', header: 'Hiring managers', defaultWidth: 200, cell: (row) => row.hiringManagerNames.join(', ') },
    latencyColumn('approvalLatency', 'Approval latency'),
    latencyColumn('decisionLatency', 'Decision latency'),
    latencyColumn('scorecardLag', 'Scorecard lag'),
    countColumn('panelistNoShows', 'Panelist no-shows'),
    countColumn('reschedulesInitiated', 'Reschedules initiated'),
    countColumn('requisitionsRaised', 'Requisitions raised'),
    countColumn('cancelledAfterStart', 'Cancelled after work started'),
  ];
  const overrideColumns: readonly Column<ScoreboardRow>[] = [
    department,
    countColumn('overrides', 'Decision rules overridden'),
    countColumn('waivers', 'Scorecards waived'),
    countColumn('skips', 'Stages skipped'),
  ];

  return (
    <>
      <TableSection id="departments" title="Departments" meta={`${data.departments.length} ${data.departments.length === 1 ? 'department' : 'departments'} · medians in working days, elapsed beneath`} action={<HowCounted metric="approval_latency" />}>
        {data.departments.length === 0 ? (
          <p className={styles.empty}>No departments under these filters.</p>
        ) : (
          <DataTable
            screenId="report-scoreboard-departments"
            label="Departments"
            columns={departmentColumns}
            rows={sortRows(data.departments, departmentSort, DEPARTMENT_SORT)}
            getRowId={(row) => row.departmentId}
            total={data.departments.length}
            sort={departmentSort}
            onSortChange={setDepartmentSort}
            selection={selection}
            onSelectionChange={setSelection}
            isSelectable={false}
            exportName="department-scoreboard"
          />
        )}
      </TableSection>

      <TableSection id="overrides" title="Overrides, waivers and skips" meta="Each opens the record, with who did it and their written reason" action={<HowCounted metric="overrides" />}>
        {data.departments.length === 0 ? (
          <p className={styles.empty}>No departments under these filters.</p>
        ) : (
          <DataTable
            screenId="report-scoreboard-overrides"
            label="Overrides, waivers and skips"
            columns={overrideColumns}
            rows={sortRows(data.departments, overrideSort, DEPARTMENT_SORT)}
            getRowId={(row) => row.departmentId}
            total={data.departments.length}
            sort={overrideSort}
            onSortChange={setOverrideSort}
            selection={selection}
            onSelectionChange={setSelection}
            isSelectable={false}
            exportName="overrides-waivers-and-skips"
          />
        )}
      </TableSection>
    </>
  );
}

const latencySort = (figure: LatencyFigure): number => figure.medianActiveDays ?? -1;

const DEPARTMENT_SORT: SortReaders<ScoreboardRow> = {
  department: (row) => row.departmentName,
  venture: (row) => row.ventureName,
  approvalLatency: (row) => latencySort(row.approvalLatency),
  decisionLatency: (row) => latencySort(row.decisionLatency),
  scorecardLag: (row) => latencySort(row.scorecardLag),
  panelistNoShows: (row) => row.panelistNoShows.value,
  reschedulesInitiated: (row) => row.reschedulesInitiated.value,
  requisitionsRaised: (row) => row.requisitionsRaised.value,
  cancelledAfterStart: (row) => row.cancelledAfterStart.value,
  overrides: (row) => row.overrides.value,
  waivers: (row) => row.waivers.value,
  skips: (row) => row.skips.value,
};

/** As the cell reads: "3 working days · 4.5 days elapsed", or nothing when nothing was counted. */
function latencyText(figure: LatencyFigure): string | null {
  if (figure.count === 0) return null;
  return `${figure.medianActiveDays ?? EMPTY} working days · ${figure.medianElapsedDays ?? EMPTY} days elapsed`;
}

type PanelistCount = 'interviews' | 'scorecards' | 'overdue' | 'noShows' | 'reschedulesInitiated';

const PANELIST_SORT: SortReaders<PanelistRow> = {
  panelist: (row) => row.name,
  departments: (row) => row.departmentNames.join(', '),
  interviews: (row) => row.interviews.value,
  scorecards: (row) => row.scorecards.value,
  submissionLag: (row) => latencySort(row.submissionLag),
  overdue: (row) => row.overdue.value,
  severity: (row) => row.severityIndex ?? -1,
  noShows: (row) => row.noShows.value,
  reschedulesInitiated: (row) => row.reschedulesInitiated.value,
};

/** Panelist activity as a table, not a card. Each figure still opens its records. */
function PanelistTable({ data }: { readonly data: Scoreboard }): ReactNode {
  const filters = data.filters;
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const countColumn = (id: PanelistCount, header: string): Column<PanelistRow> => ({
    id,
    header,
    isNumeric: true,
    isSortable: true,
    defaultWidth: 130,
    cell: (row) => <DrillCount figure={row[id]} report={PANELISTS} filters={filters} />,
    exportValue: (row) => row[id].value,
  });
  const columns: readonly Column<PanelistRow>[] = [
    { id: 'panelist', header: 'Panelist', isIdentity: true, isSortable: true, defaultWidth: 200, cell: (row) => row.name },
    { id: 'departments', header: 'Departments', isSortable: true, defaultWidth: 200, cell: (row) => row.departmentNames.join(', ') },
    countColumn('interviews', 'Interviews'),
    countColumn('scorecards', 'Scorecards'),
    {
      id: 'submissionLag',
      header: 'Submission lag',
      isSortable: true,
      defaultWidth: 200,
      cell: (row) => <DrillLatency figure={row.submissionLag} report={PANELISTS} filters={filters} />,
      exportValue: (row) => latencyText(row.submissionLag),
    },
    countColumn('overdue', 'Overdue'),
    { id: 'severity', header: 'Severity index', isSortable: true, defaultWidth: 200, cell: (row) => <Severity row={row} />, exportValue: (row) => severityText(row) },
    countColumn('noShows', 'No-shows'),
    countColumn('reschedulesInitiated', 'Reschedules initiated'),
  ];
  return (
    <TableSection id="panelists" title="Panelist activity" meta="The severity index shows from 10 scorecards in 12 months — context for calibration, never a correction" action={<HowCounted metric="severity_index" />}>
      {data.panelists.length === 0 ? (
        <p className={styles.empty}>Nobody sat on a panel in this period.</p>
      ) : (
        <DataTable
          screenId="report-scoreboard-panelists"
          label="Panelist activity"
          columns={columns}
          rows={sortRows(data.panelists, sort, PANELIST_SORT)}
          getRowId={(row) => row.userId}
          total={data.panelists.length}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={false}
          exportName="panelist-activity"
        />
      )}
      <p className={styles.meta}>
        <HowCounted metric="scorecard_lag" label="How submission lag is counted" /> · <HowCounted metric="panelist_no_shows" label="How no-shows are counted" />
      </p>
    </TableSection>
  );
}

function Severity({ row }: { readonly row: PanelistRow }): ReactNode {
  if (row.severityIndex === null) return <span className={styles.cellMeta}>{row.severityScorecards} of 10 scorecards needed</span>;
  return (
    <span className={styles.rate}>
      <span className={styles.figure}>{row.severityIndex.toFixed(2)}</span>
      {row.severityBand && <Chip tone="neutral">{SEVERITY_LABELS[row.severityBand]}</Chip>}
    </span>
  );
}

function severityText(row: PanelistRow): string {
  if (row.severityIndex === null) return `${row.severityScorecards} of 10 scorecards needed`;
  return [row.severityIndex.toFixed(2), row.severityBand ? SEVERITY_LABELS[row.severityBand] : ''].filter(Boolean).join(' · ');
}
